import { Link } from "react-router-dom";
import { DollarSign, Users, Briefcase, AlertTriangle, CreditCard } from "lucide-react";

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return "$0";
  const num = parseFloat(amount);
  if (num >= 1000) return `$${(num / 1000).toFixed(1)}k`;
  return `$${num.toLocaleString("en-AU", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

// NOTE: "Avg Job Value" and "Cancellation Fees" cards were intentionally
// dropped per the new design. Their underlying data (d.reports.financial.avg_job_value
// and d.reports.cancellations.*) is still fetched in AdminDashboard and remains
// available on the `data` object — we simply no longer render those two cards.
const cards = [
  {
    key: "revenue",
    title: "Total Revenue",
    icon: DollarSign,
    chipBg: "#DCFCE7",
    chipFg: "#15803D",
    getValue: (d) => formatCurrency(d.reports?.financial?.total_revenue),
    getSub: (d) => `${formatCurrency(d.reports?.financial?.total_fees)} platform fees`,
    getDelta: (d) => {
      const g = d.reports?.financial?.revenue_growth;
      if (g === undefined || g === null) return null;
      return { label: `${g > 0 ? "+" : ""}${g}%`, fg: "#15803D", bg: "#DCFCE7" };
    },
    link: "/szadmin/reports",
  },
  {
    key: "users",
    title: "Total Users",
    icon: Users,
    chipBg: "#DBEAFE",
    chipFg: "#2563EB",
    getValue: (d) => (d.stats?.totalUsers ?? 0).toLocaleString("en-AU"),
    getSub: (d) => `+${d.reports?.users?.new_this_month ?? 0} new this month`,
    getDelta: (d) => {
      const g = d.reports?.users?.growth;
      if (g === undefined || g === null) return null;
      return { label: `${g > 0 ? "+" : ""}${g}%`, fg: "#2563EB", bg: "#DBEAFE" };
    },
    link: "/szadmin/users",
  },
  {
    key: "activeJobs",
    title: "Active Jobs",
    icon: Briefcase,
    chipBg: "#EDE4F4",
    chipFg: "#78287C",
    getValue: (d) => (d.stats?.activeJobs ?? 0).toLocaleString("en-AU"),
    getSub: (d) => `${(d.stats?.totalJobs ?? 0).toLocaleString("en-AU")} all-time`,
    getDelta: () => null,
    link: "/szadmin/jobs",
  },
  {
    key: "pending",
    title: "Pending Actions",
    icon: AlertTriangle,
    chipBg: "#FEF3C7",
    chipFg: "#B45309",
    getValue: (d) => (d.pendingApprovals?.length ?? 0) + (d.pendingJobs?.length ?? 0),
    getSub: (d) => `${d.pendingApprovals?.length ?? 0} users · ${d.pendingJobs?.length ?? 0} jobs`,
    getDelta: () => ({ label: "Needs review", fg: "#B45309", bg: "#FEF3C7" }),
    link: "/szadmin/job-approval",
  },
  {
    key: "payments",
    title: "Payments Pending",
    icon: CreditCard,
    chipBg: "#CCFBF1",
    chipFg: "#0F766E",
    getValue: (d) => d.releasePaymentJobs?.length ?? 0,
    getSub: (d) => `${formatCurrency(d.paymentSummary?.total_upcoming_amount)} upcoming`,
    getDelta: () => null,
    link: "/szadmin/payment-release",
  },
];

const DashboardMetricCards = ({ data, loading }) => {
  return (
    <div
      className="grid gap-3.5"
      style={{ gridTemplateColumns: "repeat(auto-fill, minmax(232px, 1fr))" }}
    >
      {loading
        ? Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="sz-card h-[126px] animate-pulse" />
          ))
        : cards.map((card) => {
            const Icon = card.icon;
            const delta = card.getDelta(data);
            return (
              <Link
                key={card.key}
                to={card.link}
                className="sz-card sz-card-hover relative block overflow-hidden no-underline"
                style={{ padding: "17px 17px 16px" }}
              >
                <div className="flex items-center justify-between">
                  <div
                    className="flex h-9 w-9 items-center justify-center rounded-[10px]"
                    style={{ background: card.chipBg, color: card.chipFg }}
                  >
                    <Icon className="h-[19px] w-[19px]" strokeWidth={2} />
                  </div>
                  {delta && (
                    <span
                      className="rounded-full px-2 py-[3px] text-[11.5px] font-bold"
                      style={{ fontFamily: "Poppins,sans-serif", color: delta.fg, background: delta.bg }}
                    >
                      {delta.label}
                    </span>
                  )}
                </div>
                <div className="mt-3.5 text-[12px] font-semibold tracking-[.2px] text-[#8A90A2]">
                  {card.title}
                </div>
                <div
                  className="mt-[3px] text-[25px] font-bold text-[#0C1120]"
                  style={{ fontFamily: "Poppins,sans-serif", letterSpacing: "-.6px" }}
                >
                  {card.getValue(data)}
                </div>
                <div className="mt-[5px] text-[12px] text-[#8A90A2]">{card.getSub(data)}</div>
              </Link>
            );
          })}
    </div>
  );
};

export default DashboardMetricCards;
